import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import TrialBanner from '../components/TrialBanner';
import { getShotStats, getGameStats, getShotMonthlyTrend, getPersonalBests } from '../lib/api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  Target, 
  ChevronRight,
  Trophy,
  TrendingUp,
  Flame,
  Activity,
  Star,
  Share2,
  Loader2,
  Zap,
  Users,
  Rocket,
  ExternalLink,
  Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Shot type display config
const SHOT_TYPE_CONFIG = {
  three: { label: '3PT', color: 'bg-red-500' },
  midrange: { label: 'MID', color: 'bg-amber-500' },
  floater: { label: 'FLT', color: 'bg-purple-500' },
  layup: { label: 'LAY', color: 'bg-emerald-500' },
  freethrow: { label: 'FT', color: 'bg-blue-500' },
  // Legacy mappings
  free_throw: { label: 'FT', color: 'bg-blue-500' },
  mid_range: { label: 'MID', color: 'bg-amber-500' },
  three_point: { label: '3PT', color: 'bg-red-500' },
};

const PlayerDashboard = () => {
  const { user, refreshUser } = useAuth();
  const [shotStats, setShotStats] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState(null);
  const [personalBests, setPersonalBests] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [shotStatsRes, gameStatsRes, trendRes, bestsRes] = await Promise.all([
        getShotStats().catch(() => ({ data: null })),
        getGameStats().catch(() => ({ data: null })),
        getShotMonthlyTrend().catch(() => ({ data: null })),
        getPersonalBests().catch(() => ({ data: null }))
      ]);
      
      setShotStats(shotStatsRes.data);
      setGameStats(gameStatsRes.data);
      setMonthlyTrend(trendRes.data);
      setPersonalBests(bestsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Process shot stats by type for display
  const getShotBreakdown = () => {
    if (!shotStats?.by_type) return [];
    
    const breakdown = [];
    const byType = shotStats.by_type;
    
    // Priority order for display
    const typeOrder = ['three', 'midrange', 'floater', 'layup', 'freethrow', 'three_point', 'mid_range', 'free_throw'];
    
    // Track which types we've already added (to avoid duplicates from legacy keys)
    const addedTypes = new Set();
    
    for (const key of typeOrder) {
      if (byType[key] && byType[key].attempted > 0) {
        // Skip if we already have this type (handle legacy key mappings)
        const normalizedKey = key.replace('_', '').replace('point', '');
        if (addedTypes.has(normalizedKey)) continue;
        addedTypes.add(normalizedKey);
        
        const config = SHOT_TYPE_CONFIG[key] || { label: key.toUpperCase(), color: 'bg-gray-500' };
        breakdown.push({
          key,
          label: config.label,
          color: config.color,
          made: byType[key].made || 0,
          attempted: byType[key].attempted || 0,
          percentage: byType[key].percentage || 0,
        });
      }
    }
    
    return breakdown;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      </Layout>
    );
  }
  
  const accuracy = shotStats?.overall_percentage?.toFixed(1) || 0;
  const totalMade = shotStats?.total_made || 0;
  const totalAttempts = shotStats?.total_attempted || 0;
  const totalGames = gameStats?.total_games || 0;
  const avgPoints = gameStats?.average_points?.toFixed(1) || 0;
  const shotBreakdown = getShotBreakdown();

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="player-dashboard">
        {/* Trial Banner — shown only while user is in/just-past Shot Lab trial */}
        <TrialBanner user={user} onRefresh={refreshUser} />

        {/* Header */}
        <div className="mb-8">
          <p className="text-muted-foreground">{getGreeting()}</p>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground">
            {user?.name?.split(' ')[0] || 'Player'}
          </h1>
        </div>

        {/* Quick Action - Start Training */}
        <Link 
          to="/shots"
          className="block bg-primary text-primary-foreground rounded-2xl p-6 hover:opacity-90 transition-opacity"
          data-testid="start-training-cta"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Target size={24} />
              </div>
              <div>
                <p className="text-lg font-bold">Start Training</p>
                <p className="text-sm opacity-80">Log your shots</p>
              </div>
            </div>
            <ChevronRight size={24} />
          </div>
        </Link>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Accuracy */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Activity size={16} />
              <span className="text-xs font-medium uppercase tracking-wider">Accuracy</span>
            </div>
            <p className="font-heading text-3xl font-black text-foreground">
              {accuracy}<span className="text-lg text-muted-foreground">%</span>
            </p>
          </div>
          
          {/* Shots Made */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Target size={16} />
              <span className="text-xs font-medium uppercase tracking-wider">Shots</span>
            </div>
            <p className="font-heading text-3xl font-black text-foreground">
              {totalMade}<span className="text-lg text-muted-foreground">/{totalAttempts}</span>
            </p>
          </div>
          
          {/* Games */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Trophy size={16} />
              <span className="text-xs font-medium uppercase tracking-wider">Games</span>
            </div>
            <p className="font-heading text-3xl font-black text-foreground">{totalGames}</p>
          </div>
          
          {/* PPG */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Star size={16} />
              <span className="text-xs font-medium uppercase tracking-wider">PPG</span>
            </div>
            <p className="font-heading text-3xl font-black text-foreground">{avgPoints}</p>
          </div>
        </div>

        {/* Shot Type Breakdown */}
        {shotBreakdown.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Target className="text-primary" size={20} />
              <h2 className="font-heading text-lg font-bold text-foreground uppercase">Shot Breakdown</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {shotBreakdown.map(shot => (
                <div key={shot.key} className="bg-muted rounded-xl p-3 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${shot.color}`} />
                    <span className="text-xs font-bold uppercase text-muted-foreground">{shot.label}</span>
                  </div>
                  <p className="font-heading text-xl font-black text-foreground">
                    {shot.percentage.toFixed(0)}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {shot.made}/{shot.attempted}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress Chart */}
        {monthlyTrend?.trend && monthlyTrend.trend.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground uppercase">Progress</h2>
                <p className="text-sm text-muted-foreground">12-month accuracy trend</p>
              </div>
              <TrendingUp className="text-primary" size={24} />
            </div>
            
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyTrend.trend}>
                  <defs>
                    <linearGradient id="colorAccuracy" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="percentage" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorAccuracy)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Personal Bests - Shooting */}
        {personalBests && personalBests.best_accuracy > 0 && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Flame className="text-primary" size={24} />
              <h2 className="font-heading text-xl font-bold text-foreground uppercase">Best Shooting Session</h2>
            </div>
            
            <div className="bg-muted rounded-xl p-4 text-center">
              <p className="font-heading text-4xl font-black text-foreground">
                {personalBests.best_accuracy.toFixed(1)}%
              </p>
              <p className="text-sm text-muted-foreground mt-1">Shooting Accuracy</p>
            </div>
          </div>
        )}

        {/* Game Record Highs */}
        {personalBests && (personalBests.highest_points > 0 || personalBests.highest_rebounds > 0 || personalBests.highest_assists > 0) && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="text-primary" size={24} />
              <h2 className="font-heading text-xl font-bold text-foreground uppercase">Game Record Highs</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {personalBests.highest_points > 0 && (
                <div className="bg-muted rounded-xl p-4 text-center">
                  <p className="font-heading text-3xl font-black text-foreground">
                    {personalBests.highest_points}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">Points</p>
                </div>
              )}
              {personalBests.highest_rebounds > 0 && (
                <div className="bg-muted rounded-xl p-4 text-center">
                  <p className="font-heading text-3xl font-black text-foreground">
                    {personalBests.highest_rebounds}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">Rebounds</p>
                </div>
              )}
              {personalBests.highest_assists > 0 && (
                <div className="bg-muted rounded-xl p-4 text-center">
                  <p className="font-heading text-3xl font-black text-foreground">
                    {personalBests.highest_assists}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">Assists</p>
                </div>
              )}
              {personalBests.highest_steals > 0 && (
                <div className="bg-muted rounded-xl p-4 text-center">
                  <p className="font-heading text-3xl font-black text-foreground">
                    {personalBests.highest_steals}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">Steals</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-2 gap-4">
          <Link 
            to="/rankings"
            className="flex items-center justify-between bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition-colors"
            data-testid="rankings-link"
          >
            <div className="flex items-center gap-3">
              <Trophy size={20} className="text-primary" />
              <span className="font-medium text-foreground">Rankings</span>
            </div>
            <ChevronRight size={18} className="text-muted-foreground" />
          </Link>
          
          <Link 
            to="/share-stats"
            className="flex items-center justify-between bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition-colors"
            data-testid="share-stats-link"
          >
            <div className="flex items-center gap-3">
              <Share2 size={20} className="text-primary" />
              <span className="font-medium text-foreground">Share Stats</span>
            </div>
            <ChevronRight size={18} className="text-muted-foreground" />
          </Link>

          <Link 
            to="/share"
            className="flex items-center justify-between bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition-colors"
            data-testid="recruit-profile-link"
          >
            <div className="flex items-center gap-3">
              <Share2 size={20} className="text-primary" />
              <span className="font-medium text-foreground">Recruit Profile</span>
            </div>
            <ChevronRight size={18} className="text-muted-foreground" />
          </Link>
        </div>

        {/* Vital Resources Section */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <h2 className="font-heading text-lg font-bold text-foreground uppercase mb-4">Resources</h2>
          
          <div className="space-y-3">
            {/* RealSkill App Launch */}
            <a
              href="https://app.possibletraining.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 rounded-xl p-4 hover:border-primary/50 transition-colors group"
              data-testid="realskill-app-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Rocket size={20} className="text-primary" />
                </div>
                <div>
                  <span className="font-bold text-foreground">RealSkill App</span>
                  <p className="text-xs text-muted-foreground">I'm Possible Pro-Drill Library</p>
                </div>
              </div>
              <ExternalLink size={18} className="text-primary group-hover:translate-x-1 transition-transform" />
            </a>

            {/* RealSkill Info Page */}
            <Link
              to="/realskill-app"
              className="flex items-center justify-between bg-gradient-to-r from-purple-500/20 to-purple-500/10 border border-purple-500/30 rounded-xl p-4 hover:border-purple-500/50 transition-colors group"
              data-testid="realskill-info-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Rocket size={20} className="text-purple-400" />
                </div>
                <div>
                  <span className="font-bold text-foreground">Get RealSkill</span>
                  <p className="text-xs text-muted-foreground">$49.99/mo - Drills only</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-purple-400 group-hover:translate-x-1 transition-transform" />
            </Link>

            {/* Digital Lab */}
            <Link
              to="/digital-lab"
              className="flex items-center justify-between bg-gradient-to-r from-white/20 to-white/10 border border-white/30 rounded-xl p-4 hover:border-white/50 transition-colors group"
              data-testid="digital-lab-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Crown size={20} className="text-white" />
                </div>
                <div>
                  <span className="font-bold text-foreground">The Digital Lab</span>
                  <p className="text-xs text-muted-foreground">$150/mo - Complete package</p>
                </div>
              </div>
              <ChevronRight size={18} className="text-white group-hover:translate-x-1 transition-transform" />
            </Link>

            {/* Skool Community */}
            <a
              href="https://www.skool.com/skill-domination-x-4472"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between bg-gradient-to-r from-amber-500/20 to-amber-500/10 border border-amber-500/30 rounded-xl p-4 hover:border-amber-500/50 transition-colors group"
              data-testid="skool-community-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-amber-400" />
                </div>
                <div>
                  <span className="font-bold text-foreground">Skool Community</span>
                  <p className="text-xs text-muted-foreground">Connect with 500+ athletes</p>
                </div>
              </div>
              <ExternalLink size={18} className="text-amber-400 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PlayerDashboard;
